"use client";
import { useState, type ReactNode } from "react";
import { motion, useReducedMotion } from "motion/react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/cn";
import { glossFor, glossRegex, type GlossEntry } from "@/lib/quiz-gloss-zh";
import { SUIT_SYMBOL } from "@/lib/poker-cards";
import type { Suit } from "@/lib/flush-visual";
import { DISPLAY_FONT } from "@/components/dojo";

export type GlossVariant = "dark" | "light";

type ChipEvent = { stopPropagation: () => void; preventDefault: () => void };

const CHIP_CLS: Record<GlossVariant, string> = {
  dark: "bg-emerald-400/10 text-emerald-300 decoration-emerald-400/60 hover:bg-emerald-400/20 focus-visible:ring-emerald-400/70",
  light: "bg-emerald-600/15 text-emerald-800 decoration-emerald-700/70 hover:bg-emerald-600/25 focus-visible:ring-emerald-700/70",
};

export function GlossChip({ text, gloss, variant = "dark" }: { text: string; gloss: string; variant?: GlossVariant }) {
  const [open, setOpen] = useState(false);
  const toggle = (e: ChipEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setOpen((o) => !o);
  };
  return (
    <span className="relative inline-block align-baseline">
      <span
        role="button"
        tabIndex={0}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") toggle(e);
        }}
        title={gloss}
        aria-label={`${text}：${gloss}`}
        className={cn("mx-[1px] cursor-help rounded px-1 underline decoration-dotted underline-offset-[3px] transition focus:outline-none focus-visible:ring-1", CHIP_CLS[variant])}
      >
        {text}
      </span>
      {open && (
        <span
          role="tooltip"
          onClick={(e) => e.stopPropagation()}
          className="absolute left-0 top-full z-40 mt-1 block w-60 max-w-[78vw] rounded-lg border border-emerald-500/30 bg-zinc-950/95 p-2 text-left text-[11px] font-normal leading-relaxed text-zinc-200 shadow-xl"
        >
          <span className="font-semibold text-emerald-300">{text}</span>
          <span className="mx-1 text-zinc-600">·</span>
          {gloss}
        </span>
      )}
    </span>
  );
}

// 把文本里的术语切成「普通文本 + 可点白话 chip」。
export function renderWithGloss(text: string, variant: GlossVariant = "dark"): ReactNode {
  if (!text) return text;
  const re = glossRegex();
  const nodes: ReactNode[] = [];
  let last = 0;
  let key = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const gloss = glossFor(m[0]);
    nodes.push(gloss ? <GlossChip key={key++} text={m[0]} gloss={gloss} variant={variant} /> : m[0]);
    last = m.index + m[0].length;
    if (m[0].length === 0) re.lastIndex++;
  }
  if (last < text.length) nodes.push(text.slice(last));
  if (nodes.length === 0) return text;
  if (nodes.length === 1) return nodes[0];
  return <>{nodes}</>;
}

const isAscii = (s: string): boolean => /^[a-zA-Z0-9+-]+$/.test(s);
const escapeRe = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * 泛化点词释义:任意 GlossEntry 表 → 可点 chip 渲染。
 * 决策屏的动作词/位置名白话(O + 位置名)复用 quiz 的 GlossChip,不另造 widget。
 */
export function renderGlossEntries(
  text: string,
  entries: ReadonlyArray<GlossEntry>,
  variant: GlossVariant = "dark",
): ReactNode {
  if (!text) return text;
  const sorted = [...entries].sort((a, b) => b.term.length - a.term.length);
  const map = new Map<string, string>(sorted.map((e) => [e.term.toLowerCase(), e.gloss]));
  const src = sorted
    .map((e) => (isAscii(e.term) ? `\\b${escapeRe(e.term)}\\b` : escapeRe(e.term)))
    .join("|");
  const re = new RegExp(src, "gi");
  const nodes: ReactNode[] = [];
  let last = 0;
  let key = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const gloss = map.get(m[0].toLowerCase());
    nodes.push(gloss ? <GlossChip key={key++} text={m[0]} gloss={gloss} variant={variant} /> : m[0]);
    last = m.index + m[0].length;
    if (m[0].length === 0) re.lastIndex++;
  }
  if (last < text.length) nodes.push(text.slice(last));
  if (nodes.length === 0) return text;
  if (nodes.length === 1) return nodes[0];
  return <>{nodes}</>;
}

/**
 * ConceptBadge — 概念徽标「就地可解」(#19d4 · r1 评审阻塞)。
 *
 * 决策屏上的概念徽标(如「同花萌芽 3/5」)对零基础新手 = 无解自造词。本组件把徽标
 * 变成可点开关:点开 = 微型视觉解释卡就地展开——5 个牌位槽、已有张数按主导花色点亮
 * (色彩即语义,数格子 1 秒可感知),≤3 行白话(首行 = flushStatusLabel 全标签真值;
 * 释义行 = poker-terms-zh 权威释义单一之家,禁新造词);零导航跳离,再点即收。
 *
 * 落点说明:本应新建 concept-badge.tsx,但运行中的 dev server(Turbopack)不拾取
 * 新建源文件(500 Module not found,禁重启用户前台进程)——故落在本文件
 * (点词释义组件单一之家,poker-table/spot-prompt 均已 import,HMR 即生效)。
 */
export function ConceptBadge({
  word,
  count,
  suit,
  suitTextCls,
  pillCls,
  compact,
  pulse,
  title,
  gloss,
}: {
  /** 徽标短词(如 同花萌芽;compact 截短由调用方决定,本组件不重复截词) */
  word: string;
  /** 已有张数 n(n/5 计数,同时驱动牌位槽点亮数) */
  count: number;
  /** 主导花色(点亮槽的花色符号与着色) */
  suit: Suit;
  /** 花色符号文字色(poker-table FLUSH_SUIT_TEXT 同源传入) */
  suitTextCls: string;
  /** 徽标底色(poker-table FLUSH_PILL_COLOR 同源传入) */
  pillCls: string;
  compact: boolean;
  /** 微脉冲(听牌「差 1 张」暗示);reduce-motion 时自动静止 */
  pulse: boolean;
  /** 解释卡首行 = flushStatusLabel 全标签(单一真值,如「同花萌芽：已有3张♠，还差2张」) */
  title: string;
  /** 可选释义行(poker-terms-zh authoritativeGloss,如 后门/同花听牌);无则不渲染 */
  gloss?: string;
}) {
  const [open, setOpen] = useState(false);
  const reduce = useReducedMotion() ?? false;
  const toggle = () => setOpen((o) => !o);

  return (
    <span className="relative inline-flex">
      <motion.button
        type="button"
        aria-expanded={open}
        aria-label={`${word} ${count}/5：点开图解`}
        onClick={toggle}
        animate={pulse && !reduce ? { opacity: [1, 0.55, 1] } : undefined}
        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        className={cn(
          "flex cursor-pointer items-center whitespace-nowrap rounded-full font-semibold ring-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60",
          pillCls,
          compact ? "gap-1 px-2 py-px text-[9px]" : "gap-1.5 px-2.5 py-0.5 text-[10px]",
        )}
      >
        <span>{word}</span>
        <b
          className={cn(
            DISPLAY_FONT,
            "font-extrabold tabular-nums tracking-tight",
            compact ? "text-[10px]" : "text-[11px]",
          )}
        >
          {count}/5
        </b>
        <span aria-hidden className={cn("text-[1.15em] leading-none", suitTextCls)}>
          {SUIT_SYMBOL[suit]}
        </span>
        <ChevronDown
          aria-hidden
          className={cn("size-2.5 opacity-70 transition-transform duration-150", open && "rotate-180")}
        />
      </motion.button>

      {/* 微型视觉解释卡:牌位图形(色彩即语义)+ ≤3 行白话;再点徽标即收 */}
      {open && (
        <motion.span
          role="tooltip"
          data-testid="concept-explain-card"
          onClick={(e) => e.stopPropagation()}
          initial={reduce ? false : { opacity: 0, y: -4, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
          className="absolute left-1/2 top-full z-50 mt-1.5 block w-64 max-w-[86vw] -translate-x-1/2 rounded-xl border border-emerald-500/30 bg-zinc-950/95 p-2.5 text-left shadow-xl"
        >
          <span aria-hidden data-testid="concept-card-slots" className="flex justify-center gap-1.5">
            {Array.from({ length: 5 }, (_, i) => {
              const filled = i < count;
              return (
                <span
                  key={i}
                  data-testid="concept-card-slot"
                  data-filled={filled ? "true" : "false"}
                  className={cn(
                    "flex size-7 items-center justify-center rounded-md border text-sm font-bold",
                    filled
                      ? cn("border-solid border-white/25 bg-white/10", suitTextCls)
                      : "border-dashed border-white/25 text-white/20",
                  )}
                >
                  {SUIT_SYMBOL[suit]}
                </span>
              );
            })}
          </span>
          <span className="mt-2 block text-[11px] font-semibold leading-relaxed text-emerald-200">
            {title}
          </span>
          {gloss && (
            <span className="mt-1 block text-[11px] leading-relaxed text-zinc-300">{gloss}</span>
          )}
        </motion.span>
      )}
    </span>
  );
}
